import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { Logo } from "./Logo";
import { PHONE_TEL } from "../lib/content";

const LINKS = [
  { label: "Home", href: "#top" },
  { label: "Services", href: "#services" },
  { label: "Why Us", href: "#why" },
  { label: "About", href: "#about" },
  { label: "Industries", href: "#industries" },
  { label: "Process", href: "#process" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,backdrop-filter] duration-300 ${
        scrolled ? "bg-white/85 backdrop-blur-xl shadow-[0_8px_32px_rgba(11,46,89,0.08)]" : "bg-white/40 backdrop-blur-md"
      }`}
    >
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 lg:px-8">
        <button onClick={() => go("#top")} aria-label="Home" data-testid="nav-home">
          <Logo />
        </button>

        <div className="hidden items-center gap-8 lg:flex">
          {LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(" ", "-")}`}
              onClick={() => go(l.href)}
              className="relative text-sm font-medium text-navy/80 transition-colors hover:text-navy after:absolute after:-bottom-1.5 after:left-0 after:h-0.5 after:w-0 after:bg-gold after:transition-[width] after:duration-300 hover:after:w-full"
            >
              {l.label}
            </button>
          ))}
          <a
            href={`tel:${PHONE_TEL}`}
            data-testid="nav-call-btn"
            className="flex items-center gap-2 text-sm font-semibold text-navy transition-colors hover:text-gold"
          >
            <Phone className="h-4 w-4" /> Call Now
          </a>
          <button
            data-testid="nav-quote-btn"
            onClick={() => go("#contact")}
            className="rounded-full bg-navy px-5 py-2.5 text-sm font-semibold text-white shadow-[0_8px_24px_rgba(11,46,89,0.25)] transition-[transform,background-color] duration-200 hover:scale-[1.03] hover:bg-navy-deep"
          >
            Request a Quote
          </button>
        </div>

        <button
          data-testid="nav-hamburger"
          className="flex h-11 w-11 items-center justify-center rounded-xl bg-navy text-white lg:hidden"
          onClick={() => setOpen((v) => !v)}
          aria-label="Menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden border-t border-border bg-white lg:hidden"
          >
            <div className="flex flex-col px-5 py-4">
              {LINKS.map((l) => (
                <button
                  key={l.href}
                  data-testid={`mobile-link-${l.label.toLowerCase().replace(" ", "-")}`}
                  onClick={() => go(l.href)}
                  className="border-b border-border/60 py-3.5 text-left text-base font-medium text-navy"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="mobile-quote-btn"
                onClick={() => go("#contact")}
                className="mt-4 rounded-full bg-navy py-3.5 text-base font-semibold text-white"
              >
                Request a Quote
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
